import { createFileRoute } from "@tanstack/react-router"
import { useMutation } from "@tanstack/react-query"
import { useState } from "react"
import { Mail, Phone, MapPin, CheckCircle2, Clock } from "lucide-react"
import { api } from "@/lib/api"
import { useSettings } from "@/hooks/useSettings"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { FieldError } from "@/components/ui/field-error"
import { pageMeta } from "@/lib/seo"
import { getFieldErrors } from "@/lib/get-error-message"

export const Route = createFileRoute("/_public/contact")({
  component: Contact,
  head: () =>
    pageMeta({
      title: "Contact — Nexovia IT",
      description: "Get in touch with Nexovia IT to start your next project.",
      path: "/contact",
    }),
})

type FormState = { name: string; email: string; subject: string; message: string }

const fieldLabelClass = "text-xs font-semibold uppercase tracking-widest text-muted-foreground"
const fieldClass =
  "mt-2 rounded-xl border-border bg-background px-4 py-3 text-sm shadow-none focus-visible:border-rust focus-visible:ring-2 focus-visible:ring-rust/20"

function Contact() {
  const { data: settings } = useSettings()
  const [form, setForm] = useState<FormState>({ name: "", email: "", subject: "", message: "" })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const mutation = useMutation({
    mutationFn: async (payload: FormState) => {
      await api.post("/contact", payload)
    },
    onSuccess: () => {
      setForm({ name: "", email: "", subject: "", message: "" })
      setErrors({})
    },
    onError: (error: unknown) => {
      setErrors(getFieldErrors(error))
    },
  })

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{ background: "radial-gradient(ellipse at top, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 60%)" }}
        />
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-rust">
            <span className="h-1.5 w-1.5 rounded-full bg-rust" /> Contact
          </span>
          <h1 className="mx-auto mt-6 max-w-3xl text-5xl md:text-7xl font-bold text-balance">Let's Start Your Project.</h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            Tell us what you're building and we'll get back to you within one business day with next steps.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 md:py-28 grid gap-12 lg:grid-cols-5">
        <div className="lg:col-span-2 space-y-4">
          {[
            { icon: Mail, label: "Email", value: settings?.contact_email ?? "hello@nexoviait.com" },
            { icon: Phone, label: "Phone", value: settings?.contact_phone ?? "+1 (555) 019-2210" },
            { icon: MapPin, label: "Address", value: settings?.contact_address ?? "Remote — Global" },
            { icon: Clock, label: "Response time", value: "Within 24 hours" },
          ].map((i) => (
            <div
              key={i.label}
              className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:shadow-card"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ink text-cream">
                <i.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{i.label}</div>
                <div className="mt-1 font-medium">{i.value}</div>
              </div>
            </div>
          ))}
          {settings?.map_embed_url && (
            <div className="overflow-hidden rounded-2xl border border-border shadow-card">
              <iframe
                src={settings.map_embed_url}
                title="Location map"
                className="h-64 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          {mutation.isSuccess ? (
            <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-3 rounded-3xl border border-border bg-card p-12 text-center shadow-card">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-accent text-cream">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h2 className="mt-2 text-2xl font-bold">Message sent.</h2>
              <p className="max-w-sm text-sm text-muted-foreground">Thanks for reaching out — we'll get back to you shortly.</p>
              <Button variant="outline" onClick={() => mutation.reset()} className="mt-2 rounded-full">
                Send another message
              </Button>
            </div>
          ) : (
            <form
              className="space-y-5 rounded-3xl border border-border bg-card p-8 md:p-10 shadow-card"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                mutation.mutate(form)
              }}
            >
              <div>
                <h2 className="text-2xl font-bold">Send us a message.</h2>
                <p className="mt-1 text-sm text-muted-foreground">Fill out the form and our team will follow up shortly.</p>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <Label htmlFor="name" className={fieldLabelClass}>Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    aria-invalid={!!errors.name}
                    className={fieldClass}
                  />
                  <FieldError message={errors.name} />
                </div>
                <div>
                  <Label htmlFor="email" className={fieldLabelClass}>Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    aria-invalid={!!errors.email}
                    className={fieldClass}
                  />
                  <FieldError message={errors.email} />
                </div>
              </div>
              <div>
                <Label htmlFor="subject" className={fieldLabelClass}>Subject</Label>
                <Input
                  id="subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  aria-invalid={!!errors.subject}
                  className={fieldClass}
                />
                <FieldError message={errors.subject} />
              </div>
              <div>
                <Label htmlFor="message" className={fieldLabelClass}>Message</Label>
                <Textarea
                  id="message"
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  aria-invalid={!!errors.message}
                  className={fieldClass}
                />
                <FieldError message={errors.message} />
              </div>
              {mutation.isError && Object.keys(errors).length === 0 && (
                <p className="text-sm text-destructive">Something went wrong. Please try again.</p>
              )}
              <Button
                type="submit"
                disabled={mutation.isPending}
                className="w-full rounded-full bg-gradient-accent shadow-elegant hover:opacity-95"
              >
                {mutation.isPending ? "Sending..." : "Send message"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </>
  )
}
